import { apiFetch, apiUrl, ApiError } from "../api";

async function parseJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text.trim()) {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError("Invalid JSON response from server", response.status);
  }
}

function ensureOk(response: Response, body: unknown, fallback: string): void {
  if (!response.ok) {
    const message =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : fallback;
    throw new ApiError(message, response.status);
  }
}

export async function fetchCampaignBuilderHealth() {
  const response = await apiFetch(apiUrl("/api/campaign-builder/health"));
  const body = await parseJson(response);
  ensureOk(response, body, "Health check failed");
  return body as {
    send_mode: string;
    test_email: string;
    mcp: { ok: boolean };
    sendgrid: { configured: boolean };
  };
}

export async function fetchCampaignBuilderConfig() {
  const response = await apiFetch(apiUrl("/api/campaign-builder/config"));
  const body = await parseJson(response);
  ensureOk(response, body, "Config failed");
  return body as {
    send_mode: "test" | "prod";
    test_email: string;
    templates: { nbaa_general: string; mro_prospects: string };
  };
}

export async function fetchCampaignBuilderCampaigns() {
  const response = await apiFetch(apiUrl("/api/campaign-builder/campaigns"));
  const body = await parseJson(response);
  ensureOk(response, body, "List campaigns failed");
  return body as { results: Record<string, unknown>[] };
}

export async function createCampaignBuilderCampaign(payload: {
  name: string;
  code: string;
  description: string;
}) {
  const response = await apiFetch(apiUrl("/api/campaign-builder/campaigns"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: payload.name,
      code: payload.code,
      description: payload.description,
    }),
  });
  const body = await parseJson(response);
  ensureOk(response, body, "Create campaign failed");
  return body as { campaign: Record<string, unknown> };
}

export async function fetchCampaignBuilderCampaign(id: string) {
  const response = await apiFetch(apiUrl(`/api/campaign-builder/campaigns/${encodeURIComponent(id)}`));
  const body = await parseJson(response);
  ensureOk(response, body, "Get campaign failed");
  return body as Record<string, unknown>;
}

export type AudiencePreview = {
  qualified: Array<{
    contact_id: string;
    account_id?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    title?: string | null;
    email?: string | null;
    company?: string | null;
    account_name?: string | null;
    match_country?: string | null;
    company_segment?: string | null;
    attributions?: string[];
    match_detail?: string | null;
    reasons: string[];
  }>;
  suppressed: Array<{ contact_id: string; reasons: string[] }>;
  totals: { qualified: number; suppressed: number; evaluated: number };
  baseline_rules?: string[];
  filter?: Record<string, unknown>;
};

export async function fetchAudienceCrmOptions(campaign_key?: string, attributionScan = false) {
  const params = new URLSearchParams();
  if (campaign_key) params.set("campaign_key", campaign_key);
  if (attributionScan) params.set("attribution_scan", "1");
  const query = params.toString();
  const response = await apiFetch(apiUrl(`/api/campaign-builder/audience/crm-options${query ? `?${query}` : ""}`));
  const body = await parseJson(response);
  ensureOk(response, body, "CRM options failed");
  return body as { countries: string[]; segments: string[]; attributions: string[] };
}

export async function previewCampaignAudience(filter_json: Record<string, unknown>) {
  const response = await apiFetch(apiUrl("/api/campaign-builder/audience/preview"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filter_json, campaign_key: filter_json.campaign_key }),
  });
  const body = await parseJson(response);
  ensureOk(response, body, "Audience preview failed");
  return body as AudiencePreview;
}

export type DeliverabilityStatus =
  | "planned"
  | "staged"
  | "queued"
  | "sent"
  | "delivered"
  | "opened"
  | "clicked"
  | "bounced"
  | "deferred"
  | "dropped"
  | "spam_report"
  | "unsubscribed";

export type CampaignMessageAssignment = {
  assignment_id: string;
  marketing_campaign_id: string;
  step_id: string;
  contact_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  deliverability_status: DeliverabilityStatus;
  deliverability_updated_at: string;
  created_at: string;
  updated_at: string;
};

export type CampaignRecipientPlanRow = {
  contact_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  step_ids: string[];
  updated_at?: string;
  assignments: Array<{
    step_id: string;
    deliverability_status: DeliverabilityStatus;
    assignment_id: string;
  }>;
};

export type CampaignRecipientPlan = {
  assignments: CampaignMessageAssignment[];
  recipients: CampaignRecipientPlanRow[];
  by_step: Record<string, CampaignMessageAssignment[]>;
  deliverability_by_step: Record<string, Partial<Record<DeliverabilityStatus, number>>>;
};

export type RefreshMetricsSummary = {
  emails: number;
  looked_up: number;
  updated: number;
  unchanged: number;
  skipped_planned: number;
  window_start: string;
  window_end: string;
  note: string | null;
};

export async function refreshCampaignMetrics(campaignId: string) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/campaigns/${encodeURIComponent(campaignId)}/refresh-metrics`),
    { method: "POST" },
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Refresh Metrics failed");
  return body as { recipient_plan: CampaignRecipientPlan; summary: RefreshMetricsSummary };
}

export async function fetchCampaignRecipientPlan(campaignId: string) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/campaigns/${encodeURIComponent(campaignId)}/recipients`),
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Load recipient plan failed");
  return body as CampaignRecipientPlan;
}

export async function assignCampaignRecipients(
  campaignId: string,
  contacts: Array<{
    contact_id: string;
    email: string;
    first_name?: string | null;
    last_name?: string | null;
  }>,
  stepIds: string[],
) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/campaigns/${encodeURIComponent(campaignId)}/recipients/assign`),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ contacts, step_ids: stepIds }),
    },
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Assign recipients failed");
  return body as CampaignRecipientPlan;
}

export async function removeCampaignRecipients(
  campaignId: string,
  emails: string[],
  stepIds?: string[],
) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/campaigns/${encodeURIComponent(campaignId)}/recipients/remove`),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ emails, step_ids: stepIds }),
    },
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Remove recipients failed");
  return body as CampaignRecipientPlan;
}

export async function lockCampaignAudience(
  marketingCampaignId: string,
  filter_json: Record<string, unknown>,
  locked: boolean,
) {
  const response = await apiFetch(apiUrl("/api/campaign-builder/audience/lock"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ marketing_campaign_id: marketingCampaignId, filter_json, locked }),
  });
  const body = await parseJson(response);
  ensureOk(response, body, "Lock audience failed");
  return body;
}

export type TemplateValidationResult = {
  ok: boolean;
  template_id: string;
  name?: string;
  subject?: string;
  dynamic_attributes?: string[];
  errors?: string[];
};

export async function validateCampaignTemplate(templateId: string) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/templates/${encodeURIComponent(templateId)}/validate`),
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Validate template failed");
  return body as TemplateValidationResult & Record<string, unknown>;
}

export async function validateJourneyStep(stepId: string) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/journey-steps/${encodeURIComponent(stepId)}/validate`),
    { method: "POST" },
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Validate journey step failed");
  return body as {
    validation: TemplateValidationResult;
    step: Record<string, unknown>;
  };
}

export async function refreshJourneyStepTemplateAttributes(stepId: string) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/journey-steps/${encodeURIComponent(stepId)}/refresh-template-attributes`),
    { method: "POST" },
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Refresh template attributes failed");
  return body as {
    dynamic_attributes: string[];
    step: Record<string, unknown>;
  };
}

export type FieldMappingMode = "source" | "fixed";

export type TemplateFieldMappingRule = {
  mode: FieldMappingMode;
  source_path?: string | null;
  fixed_field_id?: string | null;
  fixed_value?: string | null;
};

export type JourneyStepTemplateFieldMappings = {
  version: 1;
  rules: Record<string, TemplateFieldMappingRule>;
  audience_overrides?: Record<string, Record<string, string>>;
  fixed_field_values?: Record<string, string>;
};

export type FieldMappingRow = {
  target: string;
  recommended_source: string | null;
  source_label: string | null;
  coverage: { populated: number; total: number; percent: number };
  sample_values: string[];
  mode?: FieldMappingMode;
  source_path?: string | null;
  fixed_field_id?: string | null;
  fixed_value?: string | null;
};

export type OurSourceField = {
  path: string;
  label: string;
  group: "contact" | "account" | "campaign" | "event";
};

export type FixedFieldCatalogItem = {
  id: string;
  label: string;
  default_value: string;
};

export type AudienceMappingPreviewRow = {
  contact_id: string;
  email: string;
  name: string;
  cells: Record<string, string>;
};

export type JourneyStepFieldMapping = {
  send_from: string;
  dynamic_attributes: string[];
  universe_total: number;
  mappings: FieldMappingRow[];
  source_fields: OurSourceField[];
  fixed_fields: FixedFieldCatalogItem[];
  saved_mappings: JourneyStepTemplateFieldMappings;
  audience_preview: AudienceMappingPreviewRow[];
  audience_preview_truncated: boolean;
  audience_sourced_targets: string[];
};

export async function fetchJourneyStepFieldMapping(
  stepId: string,
  filter_json: Record<string, unknown>,
  campaign_key?: string,
  preview?: {
    rules?: Record<string, TemplateFieldMappingRule>;
    audience_overrides?: Record<string, Record<string, string>>;
    fixed_field_values?: Record<string, string>;
    audience_preview_limit?: number;
  },
) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/journey-steps/${encodeURIComponent(stepId)}/field-mapping`),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filter_json,
        campaign_key,
        rules: preview?.rules,
        audience_overrides: preview?.audience_overrides,
        fixed_field_values: preview?.fixed_field_values,
        audience_preview_limit: preview?.audience_preview_limit,
      }),
    },
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Load field mapping failed");
  return body as JourneyStepFieldMapping;
}

export async function saveJourneyStepFieldMappings(
  stepId: string,
  template_field_mappings: JourneyStepTemplateFieldMappings,
  send_from?: string,
) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/journey-steps/${encodeURIComponent(stepId)}/field-mappings`),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ template_field_mappings, send_from }),
    },
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Save field mappings failed");
  return body as { step: Record<string, unknown> };
}

export async function sendCampaignTest(
  stepId: string,
  to: string,
  options?: {
    dynamic_template_data?: Record<string, string>;
    filter_json?: Record<string, unknown>;
    campaign_key?: string;
    send_from?: string;
  },
) {
  const response = await apiFetch(apiUrl("/api/campaign-builder/send/test"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      step_id: stepId,
      to,
      dynamic_template_data: options?.dynamic_template_data,
      filter_json: options?.filter_json,
      campaign_key: options?.campaign_key,
      send_from: options?.send_from,
    }),
  });
  const body = await parseJson(response);
  ensureOk(response, body, "Test send failed");
  return body as { step?: Record<string, unknown>; actual_to?: string; dynamic_template_data?: Record<string, string> };
}

export async function completeJourneyStepTest(stepId: string) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/journey-steps/${encodeURIComponent(stepId)}/test-complete`),
    { method: "POST" },
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Mark test completed failed");
  return body as { step: Record<string, unknown> };
}

export async function completeJourneyStepMappingReview(stepId: string) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/journey-steps/${encodeURIComponent(stepId)}/mapping-review`),
    { method: "POST" },
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Acknowledge mappings failed");
  return body as { step: Record<string, unknown> };
}

export async function fetchContactCommunications(contactId: string) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/contacts/${encodeURIComponent(contactId)}/communications`),
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Communications failed");
  return body as { results: Record<string, unknown>[] };
}

export async function sendCampaignToContacts(
  stepId: string,
  contactIds: string[],
  emails: Record<string, string>,
) {
  const response = await apiFetch(apiUrl("/api/campaign-builder/send/contacts"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ step_id: stepId, contact_ids: contactIds, emails }),
  });
  const body = await parseJson(response);
  ensureOk(response, body, "Contact send failed");
  return body;
}

export async function sendCampaignAudience(
  stepId: string,
  filter_json: Record<string, unknown>,
  options?: { send_from?: string },
) {
  const response = await apiFetch(apiUrl("/api/campaign-builder/send/audience"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ step_id: stepId, filter_json, send_from: options?.send_from }),
  });
  const body = await parseJson(response);
  ensureOk(response, body, "Audience send failed");
  return body;
}

export async function explainAudienceContact(contactId: string, filter_json: Record<string, unknown>) {
  const params = new URLSearchParams({
    contact_id: contactId,
    filter_json: JSON.stringify(filter_json),
  });
  const response = await apiFetch(apiUrl(`/api/campaign-builder/audience/explain?${params}`));
  const body = await parseJson(response);
  ensureOk(response, body, "Explain failed");
  return body as { contact_id: string; included: boolean; reasons: string[] };
}

export async function upsertCampaignJourneyStep(payload: Record<string, unknown>) {
  const response = await apiFetch(apiUrl("/api/campaign-builder/journey-steps"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await parseJson(response);
  ensureOk(response, body, "Save journey step failed");
  return body as { step: Record<string, unknown> };
}

export async function fetchCampaignTemplatePreview(templateId: string) {
  const response = await apiFetch(
    apiUrl(`/api/campaign-builder/templates/${encodeURIComponent(templateId)}/preview`),
  );
  const body = await parseJson(response);
  ensureOk(response, body, "Template preview failed");
  return body as { subject?: string; html?: string };
}
